<template>
  <div class="video-material-info">
    <InfoTitle
      title="素材参数"
      button-text="更换素材"
      :fn="handleMaterialEdit"
    />
    <Divider />
    <InfoTitle title="视频设置" />
    <LabelSlider label="透明度" :value="effectInfo.opacity" type="opacity" />
    <LabelSlider
      label="亮度"
      :value="effectInfo.brightness"
      type="brightness"
      :disabled="isBackground"
      :step="0.01"
    />
    <LabelInput
      label="旋转"
      :deg="true"
      :value="effectInfo.rotate"
      type="rotate"
      :disabled="isBackground"
      :precision="0"
    />
    <LabelInput
      label="圆角半径"
      v-model="currentEffect.content.radius"
      :precision="0"
      :disabled="isBackground"
    />
    <LabelSwitch label="阴影" v-model="currentEffect.content.shadow" />
    <template v-if="currentEffect.content.shadow">
      <div class="row-box">
        <LabelColor
          label="颜色"
          class="shadow-color"
          v-model="currentEffect.content.shadowColor"
          :showAlpha="false"
        />
        <LabelInput
          label="模糊"
          class="text-shadow-blurry"
          v-model="currentEffect.content.shadowBlur"
          :min="0"
        />
      </div>
      <div class="row-box">
        <div class="shadow-offset">
          <label>偏移</label>
        </div>
        <LabelInput
          label="x"
          class="shadow-offset-x"
          v-model="currentEffect.content.shadowX"
        />
        <LabelInput
          label="y"
          class="shadow-offset-y"
          v-model="currentEffect.content.shadowY"
        />
      </div>
    </template>
    <flip-editor
      v-model="currentEffect.content.flip"
      :disabled="isBackground"
    />
    <position-editor :effect="currentEffect" :disabled="isBackground" />
    <label-button
      :text="container ? '适应容器' : '适应画布'"
      :disabled="isBackground || (container && container.type === 'group')"
      @click="fitCanvas"
    >
      大小
    </label-button>
    <LabelInput
      label="播放速率"
      :value="currentEffect.content.playbackRate"
      @change="handlePlaybackRateChange"
      :max="10"
      :min="0.1"
      :step="0.1"
    />
    <LabelOption
      label="防抖"
      :optionList="antiShakeList"
      v-model="currentEffect.content.shakiness"
      @change="shakinessChange"
      :loading="shakinessLoad"
      :disabled="shakinessLoad"
    >
      <span v-if="shakinessLoad" class="closeBtn" @click="shakinessLoadClose">取消</span>
    </LabelOption>
    <Divider />
    <template v-if="!currentEffect.container">
      <InfoTitle title="视频转场效果" />
      <LabelOption
        label="入场效果"
        :optionList="enterEffectList"
        v-model="currentEffect.enter"
        :disabled="isBackground"
      />
      <LabelInput
        label="入场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.enterDuration"
        :disabled="currentEffect.enter === 'none' || isBackground"
      />
      <LabelOption
        label="出场效果"
        :optionList="leaveEffectList"
        v-model="currentEffect.leave"
        :disabled="isBackground"
      />
      <LabelInput
        label="出场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.leaveDuration"
        :disabled="currentEffect.leave === 'none' || isBackground"
      />
      <Divider />
    </template>
    <InfoTitle title="视频编辑" />
    <label-button text="截取" @click="showCropModal">视频截取</label-button>
    <label-button
      text="裁剪"
      @click="showImageCropModal"
      :disabled="isBackground || currentEffect.hidden"
      class="image-crop-ctrl"
    >
      视频裁剪
      <a-button
        class="label-button__btn"
        @click="clearImageCrop"
        :disabled="!currentEffect.content.clip"
      >
        重置
      </a-button>
    </label-button>
    <label-button text="添加滤镜" @click="showFilters" :disabled="isBackground">
      滤镜
    </label-button>
    <label-button
      text="画面选择"
      @click="showContainerGrids"
      :disabled="
        isBackground || currentEffect.container || currentEffect.hidden
      "
    >
      画中画
    </label-button>
    <label-button
      text="分离"
      @click="splitAudio"
      :disabled="
        isBackground ||
        currentEffect.container ||
        currentEffect.hidden ||
        isMuted
      "
    >
      分离音频
    </label-button>
    <Divider />
    <CollapseTitle title="视频音量调整" :open="true">
      <LabelSlider
        label="视频音量"
        :useModel="true"
        :value="currentEffect.content.volume"
        @change="handleVolume"
        type="volume"
        :max="200"
        :step="1"
        percentage
        :disabled="isBackground"
      />
      <LabelCheckbox
        label="视频静音播放"
        :value="isMuted"
        @change="handleMute"
        :disabled="isBackground"
      />
      <label-switch label="音量淡入" v-model="currentEffect.content.fadeIn" />
      <LabelInput
        label="淡入时长 (s)"
        :time="true"
        v-model="currentEffect.content.fadeInDuration"
        :disabled="!currentEffect.content.fadeIn"
      />
      <label-switch label="音量淡出" v-model="currentEffect.content.fadeOut" />
      <LabelInput
        label="淡出时长 (s)"
        :time="true"
        v-model="currentEffect.content.fadeOutDuration"
        :disabled="!currentEffect.content.fadeOut"
      />
    </CollapseTitle>
    <CollapseTitle title="背景音乐音量调整" :open="true">
      <!-- <p>The minimum of music volumes will be used for intersecting videos.</p> -->
      <LabelSlider
        label="背景音乐"
        :useModel="true"
        v-model="myVideoMusicVolume"
        type="backgroundVolume"
        :max="100"
        :step="1"
        percentage
        :disabled="isBackground"
      />
      <LabelCheckbox
        label="视频播放时关闭背景音乐"
        v-model="myVideoMusicMute"
        :disabled="isBackground"
      />
    </CollapseTitle>
    <Divider />
    <InfoTitle title="视频显示" />
    <template v-if="!currentEffect.container">
      <LabelInput
        label="延迟出现（秒）"
        v-model="displayStart"
        :time="true"
        :disabled="isBackground"
      />
      <LabelInput
        label="显示时长（秒）"
        v-model="displayDuration"
        :time="true"
        :disabled="isBackground"
      />
    </template>
    <div class="checkbox-tips">
      <LabelCheckbox
        label="视频循环播放"
        :value="currentEffect.content.loop"
        @change="handleLoopChange"
      />
      <a-tooltip>
        <div class="icon iconfont">&#xe62f;</div>
        <template slot="title">
          视频于本场景循环播放
          <br />循环播放的视频不支持分割。
        </template>
      </a-tooltip>
    </div>
    <div class="setting">
      <LabelCheckbox
        label="设为画布背景视频"
        v-model="isBackground"
        :disabled="currentEffect.container"
      />
    </div>
    <!-- <a-button
      block
      class="toggle-button"
      @click="selectContainer"
      v-if="currentEffect.container"
    >选择容器</a-button>-->
    <template v-if="!currentEffect.container">
      <Divider />
      <InfoTitle title="视频动态效果" />
      <LabelSwitch
        label="视频动态特效"
        v-model="currentEffect.showTracks"
        :fn="handleTrackShow"
        :disabled="isBackground"
      />
      <SingleEffectEditor
        label="视频特效"
        v-for="(track, i) in currentEffect.tracks"
        :disabled="!currentEffect.showTracks || isBackground"
        :key="i"
        :track="track"
        :effectList="effectListAt(i)"
        @delete="removeTrack(i)"
        :deletable="currentEffect.tracks.length > 1"
      />
      <a-button
        v-if="currentEffect.tracks.length < 5"
        class="insert-track"
        @click="insertTrack"
        block
        :disabled="!currentEffect.showTracks || isBackground"
        icon="plus"
        >继续添加</a-button
      >
    </template>
    <div v-show="false" />
    <video-crop-modal
      :visible.sync="cropShowing"
      :src="effectInfo.src"
      :value="displayCrop"
      @change="handleCropChange"
    />
    <image-crop-modal
      :visible.sync="imageCropVisible"
      :src="imageSrc"
      :value="currentEffect.content.clip"
      @change="handleImageCropChange"
      title="视频裁剪"
    />
    <container-grid-convert
      :visible.sync="containerGridVisible"
      @ok="handleContainerGridOK"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from "vuex";
import InfoTitle from "../base/InfoTitle";
import Divider from "../base/Divider";
import LabelSwitch from "../base/LabelSwitch";
import SingleEffectEditor from '../base/SingleEffectEditor';
import LabelSlider from "../base/LabelSlider";
import LabelInput from "../base/LabelInput";
import LabelOption from "../base/LabelOption";
import LabelCheckbox from "../base/LabelCheckbox";
import LabelButton from "../base/LabelButton";
import LabelColor from "../base/LabelColor";
import VideoCropModal from "./VideoCropModal";
import PositionEditor from "../base/PositionEditor";
import FlipEditor from '../base/FlipEditor';
import CollapseTitle from "../base/CollapseTitle";
import { clamp, encodeURIIfNeeded, getVideoAspectRatio, getVideoDuration } from "../../utils";
import { fitCanvas } from '../../utils/util';
import { effectParams } from '../../utils/textEffectMap';
import ImageCropModal from './ImageCropModal';
import ContainerGridConvert from './ContainerGridConvert';
import { containerGrids } from '../../utils/shapes';
import { post } from '../../utils/api';
import webSocket from '../../utils/webSocket';
export default {
  name: "VideoMaterialInfo",
  components: {
    InfoTitle,
    Divider,
    LabelSwitch,
    SingleEffectEditor,
    LabelSlider,
    LabelInput,
    LabelOption,
    LabelCheckbox,
    LabelButton,
    LabelColor,
    PositionEditor,
    VideoCropModal,
    FlipEditor,
    CollapseTitle,
    ImageCropModal,
    ContainerGridConvert
  },
  data() {
    return {
      cropShowing: false,
      volumeShow: false,
      imageCropVisible: false,
      enterEffectList: [
        { name: '无', value: 'none' },
        { name: '淡入', value: 'enter' }
      ],
      antiShakeList:[
      { name: '选择防抖等级', value: 'none' },
      { name: '轻度', value: '1' },
      { name: '推荐', value: '5' },
      { name: '较平稳', value: '7' },
      { name: '最平稳', value: '10' },
      ],
      leaveEffectList: [
        { name: '无', value: 'none' },
        { name: '淡出', value: 'enter' }
      ],
      cropVisible: false,
      containerGridVisible: false,
      shakinessLoad:false
    };
  },
  computed: {
    ...mapState(['effectNames', 'generalEffects', 'effects', 'screenRatio', 'shapeNames']),
    ...mapGetters(["activeFragment", "effectInfo", "currentEffect"]),
    screenWidth() {
      return 480 * this.screenRatio;
    },
    displayStart: {
      get() {
        return this.currentEffect.start;
      },
      set(value) {
        this.$set(this.currentEffect, "start", Math.max(value, 0));
      },
    },
    displayDuration: {
      get() {
        return this.currentEffect.duration;
      },
      set(value) {
        this.$set(
          this.currentEffect,
          "duration",
          Math.max(value, 400)
        );
      },
    },
    actualPlaybackRate() {
      return clamp(+this.currentEffect.content.playbackRate || 1, .1, 10);
    },
    displayCrop() {
      const { content: efc, duration, container } = this.currentEffect;
      const containerEffect = container && this.effects[container];
      const usedDuration = containerEffect ? containerEffect.duration : duration;

      if (efc.loop && efc.crop) {
        return efc.crop;
      }
      if (efc.crop) {
        return [efc.crop[0], efc.crop[0] + usedDuration / 1e3 * this.actualPlaybackRate];
      }
      return [0, usedDuration / 1e3 * this.actualPlaybackRate];
    },
    isBackground: {
      get() {
        return this.activeFragment.background === this.currentEffect.id;
      },
      set(value) {
        if (value) {
          this.$store.dispatch("setBackground");
        } else {
          this.$store.dispatch("unsetBackground");
        }
      },
    },
    myVideoMusicVolume: {
      get() {
        const efc = this.currentEffect.content;
        return efc.musicVolume;
      },
      set(value) {
        this.$set(this.currentEffect.content, 'musicVolume', value);
      }
    },
    myVideoMusicMute: {
      get() {
        return this.myVideoMusicVolume === 0;
      },
      set(value) {
        this.myVideoMusicVolume = value ? 0 : 100;
      }
    },
    imageSrc() {
      return encodeURIIfNeeded(this.currentEffect.content.src) + '?x-oss-process=video/snapshot,t_1000,f_jpg';
    },
    container() {
      const { container } = this.currentEffect;
      if (container) {
        const containerEf = this.effects[container];
        return containerEf;
      }
      return null;
    },
    isMuted: {
      get() {
        return this.currentEffect.content.volume === 0;
      },
      set(value) {
        this.currentEffect.content.volume = value ? 0 : 100;
      }
    }
  },
  methods: {
    ...mapActions(["setEditStatus", "handleMaterielItemStyle"]),
    ...mapMutations(['handleFilterShowing']),
    handleMaterialEdit() {
      this.handleFilterShowing(false);
      this.setEditStatus(true);
    },
    handleTrackShow(value) {
      if (value) {
        this.$set(this.currentEffect.tracks[0], 'start', this.currentEffect.start);
      }
    },
    async shakinessLoadClose(){
        var _t =this;
        post('material/cancel-shake', {
          id:_t.effects[_t.currentEffect.id].shakinessId
        }).then(()=>{
          _t.shakinessLoad = false;
          _t.$message({
            message: "取消成功!",
            type: "success",
            duration: 3000,
          });
          _t.currentEffect.content.shakiness = 'none'
          delete _t.effects[_t.currentEffect.id].shakinessId;
          _t.$store.commit('handleEffects', {
            ..._t.$store.state.effects
          });
        });
    },
    async shakinessChange(e){
      if(e=='none'){
        return;
      }
      var _t = this;
      try{
        _t.shakinessLoad = true;
        _t.$message({
            message: "视频正在转换中，请稍后!",
            type: "success",
            duration: 3000,
        });
        var data = await post('material/create-shake', {
          material_url: _t.currentEffect.content.src,
          shakiness: Number(e)
        });
        if(data.url){
          _t.effects[_t.currentEffect.id].content.src = data.url;
          _t.shakinessLoad = false;
        }else{
          _t.effects[_t.currentEffect.id].shakinessId = data.id;
          _t.$store.commit('handleEffects', {
            ..._t.$store.state.effects
          });
        }
      }catch(e){
        console.log(e)
        _t.shakinessLoad = false;
      }
    },
    async listenerWebscoket() {
      var _t = this;
      this.websocket = await webSocket();
      this.websocket.addEventListener('message', function (event) {
        if (event.data) {
          var data = JSON.parse(event.data)
          if (data.event_type == 7) {
            if(data.data.url){
              Object.keys(_t.effects).map(a=>{
                  if(_t.effects[a].shakinessId==data.data.id){
                    _t.effects[a].content.src = data.data.url;
                    delete _t.effects[a].shakinessId;
                    _t.shakinessLoad = false;
                  }
              })
            }
          }
        }
      });
    },
    insertTrack() {
      let effectType = '';
      for (var i of this.generalEffects) {
        if (this.currentEffect.tracks.every(tr => {
          switch (tr.effectType) {
            case 'slide':
            case 'slideHorizon':
              return i !== 'slide' && i !== 'slideHorizon';
          }
          return i !== tr.effectType;
        })) {
          effectType = i;
          break;
        }
      }
      if (effectType) {
        this.currentEffect.tracks.push({
          id: +new Date() + '',
          effectType,
          params: effectParams[effectType] ? { ...effectParams[effectType] } : null,
          start: 1e3,
          duration: 1e3
        });
      } else {
        console.log('No effect was added');
      }
    },
    removeTrack(i) {
      this.$set(
        this.currentEffect,
        'tracks',
        this.currentEffect.tracks.slice(0, i).concat(this.currentEffect.tracks.slice(i + 1))
      )
    },
    effectListAt(i) {
      return this.generalEffects
        .map(v => {
          let disabled = false;
          switch (v) {
            case 'slide':
            case 'slideHorizon':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && (tr.effectType === 'slide' || tr.effectType === 'slideHorizon'));
              break;
            case 'scale':
            case 'gradientFade':
            case 'gradientHide':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && tr.effectType === v);
              break;
          }
          return {
            name: this.effectNames[v],
            value: v,
            disabled
          };
        });
    },
    handleCropperInitialize() {
      this.initialized = true;
    },
    showCropModal() {
      this.cropShowing = true;
    },
    handleVolume(value) {
      this.handleMaterielItemStyle({ type: "volume", value: value });
    },
    handleMute(value) {
      this.isMuted = value;
    },
    setDuration(duration) {
      this.$set(this.currentEffect, 'duration', duration);

      if (this.currentEffect.container) {
        const container = this.effects[this.currentEffect.container];
        if (container) {
          this.$set(container, 'duration', clamp(container.duration, 400, duration));
        }
      }
    },
    handleCropChange({ start, end, total }) {
      const efc = this.currentEffect.content;
      if (efc.loop) {
        this.$set(efc, 'crop', [start, end]);
      } else {
        this.$set(efc, 'crop', [start, total]);
        const duration = Math.max((end - start) * 1e3 / this.actualPlaybackRate, 400);
        this.setDuration(duration);
      }
    },
    handlePlaybackRateChange(val) {
      const efc = this.currentEffect.content;
      const usedPlaybackRate = clamp(+val || 1, .1, 10);
      this.$set(efc, 'playbackRate', usedPlaybackRate);
      if (efc.crop && !efc.loop) {
        const [start, end] = efc.crop;
        const duration = (end - start) * 1e3 / usedPlaybackRate;
        const container = this.currentEffect.container && this.effects[this.currentEffect.container];
        const curDuration = container ? container.duration : this.currentEffect.duration;
        if (duration < curDuration) {
          this.setDuration(duration);
        }
      }
    },
    async handleLoopChange(val) {
      const efc = this.currentEffect.content;
      if (val) {
        this.$set(efc, 'crop', [].concat(this.displayCrop));
        this.$set(efc, 'loop', true);
      } else {
        const duration = await getVideoDuration(efc.src);
        this.$set(efc, 'crop', [efc.crop[0], duration]);
        this.$set(this.currentEffect, 'duration', clamp((duration - efc.crop[0]) * 1e3 / this.actualPlaybackRate, 400, this.currentEffect.duration));
        this.$set(efc, 'loop', false);
      }
    },
    fitCanvas() {
      const efc = this.currentEffect.content;
      const { container } = this;
      if (container) {
        const cefc = container.content;
        let width = cefc.width;
        let height = cefc.height;
        const containerAspectRatio = width / height;
        if (!efc.repeat) {
          const effectAspectRatio = efc.width / efc.height;
          if (effectAspectRatio > containerAspectRatio) {
            width = height * effectAspectRatio;
          } else {
            height = width / effectAspectRatio;
          }
        }

        efc.width = width;
        efc.height = height;
        efc.top = (cefc.height - height) / 2;
        efc.left = (cefc.width - width) / 2;
        return;
      }
      const { top, left, width, height } = fitCanvas(efc, 480 * this.$store.state.screenRatio, 480);

      efc.height = height;
      efc.width = width;
      efc.top = top;
      efc.left = left;
    },
    showFilters() {
      const efc = this.currentEffect.content;

      if (!efc.filterParams) {
        this.$set(efc, 'filterParams', {
          blur: 0,
          contrast: 100,
          grayscale: false,
          invert: false
        });
      }
      this.handleFilterShowing(true);
    },
    showImageCropModal() {
      if (this.currentEffect.locked) {
        this.imageCropVisible = true;
        return;
      }
      this.$store.commit('handleClipping', true);
      this.$store.commit('handleClippingEffect', this.currentEffect);
    },
    clearImageCrop() {
      const efc = this.currentEffect.content;
      if (efc.clip) {
        if (!efc.repeat) {
          getVideoAspectRatio(efc.src).then(aspectRatio => {
            this.$set(efc, 'clip', null);
            efc.width = efc.height * aspectRatio;
          });
        }
      }
    },
    handleImageCropChange({ x, y, w, h }) {
      const efc = this.currentEffect.content;
      const prevClip = efc.clip || {
        w: efc.width,
        h: efc.height
      };
      this.$set(efc, 'clip', { x, y, w, h });
      efc.height *= h / prevClip.h;
      efc.width *= w / prevClip.w;
    },
    showContainerGrids() {
      this.containerGridVisible = true;
    },
    handleContainerGridOK(e) {
      const arr = containerGrids[e]({
        w: this.screenWidth,
        h: 480,
        shapeNames: this.shapeNames
      });
      const curId = +new Date();
      const effects = {};

      const prevEfc = this.currentEffect.content;
      const effectAspectRatio = prevEfc.width / prevEfc.height;

      const effectIds = arr.map((efc, i) => {
        const id = curId + 'g' + i;
        const cid = curId + 'c' + i;
        let width = efc.width;
        let height = efc.height;
        const containerAspectRatio = width / height;
        if (!prevEfc.repeat) {
          if (effectAspectRatio > containerAspectRatio) {
            width = height * effectAspectRatio;
          } else {
            height = width / effectAspectRatio;
          }
        }
        const childEffect = {
          ...this.currentEffect,
          id: cid,
          container: id,
          content: {
            ...this.currentEffect.content,
            top: (efc.height - height) / 2,
            left: (efc.width - width) / 2,
            width,
            height
          },
          showTracks: false
        };
        const effect = {
          ...this.currentEffect,
          id,
          type: 'container',
          locked: false,
          hidden: false,
          content: efc,
          children: [cid],
          enter: 'none',
          enterDuration: 1e3,
          leave: 'none',
          leaveDuration: 1e3,
          showTracks: false
        };
        effects[id] = effect;
        effects[cid] = childEffect;
        return id;
      });

      this.$store.commit('handleEffects', {
        ...this.$store.state.effects,
        ...effects
      });

      this.activeFragment.effects = [].concat.apply([], this.activeFragment.effects.map(id => {
        if (id === this.currentEffect.id) {
          return effectIds;
        }
        return id;
      }));

      this.$store.commit('handleEffectSelect', effectIds);
      this.containerGridVisible = false;
    },
    splitAudio() {
      const curId = (+new Date()) + '';
      const efc = this.currentEffect.content;
      const prevVolume = efc.volume;
      const prevMusicVolume = efc.prevMusicVolume;
      efc.volume = 0;
      efc.musicVolume = 100;

      this.$store.dispatch('addFragmentAudio', {
        audioId: curId,
        name: efc.name,
        src: efc.src,
        loop: efc.loop,
        volume: prevVolume,
        duration: efc.duration,
        crop: efc.crop,
        musicVolume: prevMusicVolume,
        playbackRate: efc.playbackRate,
        fadeIn: efc.fadeIn,
        fadeInDuration: efc.fadeInDuration,
        fadeOut: efc.fadeOut,
        fadeOutDuration: efc.fadeOutDuration
      });
    }
  },
  mounted() {
    var _t = this;
    _t.listenerWebscoket();
    if(_t.effects[_t.currentEffect.id].shakinessId){
      _t.shakinessLoad = true;
    }
  },
  beforeDestroy() {
    this.setEditStatus(false);
  }
};
</script>

<style scoped>
.video-material-info {
  padding: 11px 12px 11px 13px;
  background: rgba(248, 248, 248, 1);
}

.checkbox-tips {
  display: flex;
  align-items: flex-end;
  position: relative;
}

.icon {
  font-size: 12px;
  color: #e4b634;
  cursor: pointer;
}

.toggle-button {
  margin-top: 12px;
  height: 36px;
  border-radius: 2px;
}

/* .icon-play-btn{
  font-size: 20px;
  color: #000;
} */

.row-box {
  font-size: 12px;
  color: #656667;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.video-material-info ::v-deep .setting-input {
  margin-left: 3px;
}

.shadow-color,
.shadow-offset {
  margin-top: 10px;
}

.insert-track {
  margin-top: 12px;
}

.image-crop-ctrl ::v-deep .label-button__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 6px;
}
.closeBtn{
  font-size: 12px;
  cursor: pointer;
  margin-left:3px;
}
</style>
